import moment from 'moment';
import 'moment/locale/pl';

import { getLocale } from './i18n';
import { toProperCaseAllWords } from './nlp';

const humanizeTimeLeft = (date: string) => {
  moment.locale(getLocale());
  const now = moment();
  const inputDate = moment.utc(date).local();
  const diff = inputDate.diff(now);

  return moment.duration(diff).humanize();
};

const currentDateIsoFormat = () => {
  return new Date().toISOString();
};

const secondsToMinutes = (seconds) => {
  return Math.round(seconds / 60);
};

const secondsToTimeString = (seconds, withUnits: boolean = false) => {
  // Time calculations for days, hours, minutes and seconds
  const timeStringParts = [];

  const hours = Math.floor(seconds / 3600);
  pushTimeOrRemainingZeros(timeStringParts, hours, withUnits ? 'h' : '');

  const minutes = Math.floor((seconds % 3600) / 60);
  pushTimeOrRemainingZeros(timeStringParts, minutes, withUnits ? 'm' : '');

  const sec = seconds % 60;
  pushTimeOrRemainingZeros(timeStringParts, sec, withUnits ? 's' : '');

  if (timeStringParts.length === 0) {
    timeStringParts.push('0');
  }

  if (timeStringParts[0] === '') {
    timeStringParts.shift();
  }
  if (withUnits) {
    return timeStringParts.join(' ');
  } else {
    return timeStringParts.join(':');
  }
};

const pushTimeOrRemainingZeros = (timeStringParts, time, unit: string = '') => {
  let value = '';
  if (time > 0) {
    if (timeStringParts.length > 0) {
      value = time.toString().padStart(2, '0');
    } else {
      value = time.toString();
    }
  } else if (timeStringParts.length > 0) {
    value = '00';
  }

  if (unit) {
    value = `${value}${unit}`;
  }

  timeStringParts.push(value);
};

const humanizeDate = (utcDate, translator, alwaysYear = false) => {
  moment.locale(getLocale());
  const now = moment();
  const inputDate = moment.utc(utcDate).local();

  // For today's date, just return the hour.
  if (inputDate.isSame(now, 'day')) {
    return translator('humanizedDate.today');
  }

  // For yesterday's date, return the word 'yesterday' and the hour.
  if (inputDate.isSame(now.clone().subtract(1, 'day'), 'day')) {
    return translator('humanizedDate.yesterday');
  }

  // For tomorrows's date, return the word 'tomorrow' and the hour.
  if (inputDate.isSame(now.clone().subtract(-1, 'day'), 'day')) {
    return translator('humanizedDate.tomorrow');
  }

  // For dates within this year, return day, month
  if (inputDate.isSame(now, 'year') && !alwaysYear) {
    return toProperCaseAllWords(inputDate.format('dd, Do MMM'));
  }

  // For older dates, return day, month, year
  return toProperCaseAllWords(inputDate.format('dd, Do MMM YYYY'));
};

const humanizeDateShort = (utcDate, translator) => {
  moment.locale(getLocale());
  const now = moment();
  const inputDate = moment.utc(utcDate).local();

  // For today's date, just return the hour.
  if (inputDate.isSame(now, 'day')) {
    return translator('humanizedDate.today');
  }

  // For yesterday's date, return the word 'yesterday' and the hour.
  if (inputDate.isSame(now.clone().subtract(1, 'day'), 'day')) {
    return translator('humanizedDate.yesterday');
  }

  // For dates within this year, return day, month
  if (inputDate.isSame(now, 'year')) {
    return toProperCaseAllWords(inputDate.format('D MMM'));
  }

  // For older dates, return day, month, year
  return toProperCaseAllWords(inputDate.format('D MMM YYYY'));
};

const humanizeTime = (utcDate) => {
  return moment.utc(utcDate).local().format('LT');
};

const formatTime = (time: number) => {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const dateToYYYYMMDD = (date: Date, separator: string = '-') => {
  return moment(date).format(`YYYY${separator}MM${separator}DD`);
};

/**
 * Checks if a date is today or in the past
 */
const isDateTodayOrPast = (date: string | Date): boolean => {
  const inputDate = moment.utc(date).local().startOf('day');
  const today = moment().startOf('day');
  return inputDate.isSameOrBefore(today);
};

/**
 * Checks if a date is today
 */
const isToday = (date: string | Date): boolean => {
  return moment.utc(date).local().isSame(moment(), 'day');
};

/**
 * Checks if a date is in the past (before today)
 */
const isPast = (date: string | Date): boolean => {
  const inputDate = moment.utc(date).local().startOf('day');
  const today = moment().startOf('day');
  return inputDate.isBefore(today);
};

/**
 * Checks if a date is in the future (after today)
 */
const isFuture = (date: string | Date): boolean => {
  const inputDate = moment.utc(date).local().startOf('day');
  const today = moment().startOf('day');
  return inputDate.isAfter(today);
};

/**
 * Returns the start of day for a given date
 */
const startOfDay = (date: string | Date): Date => {
  return moment.utc(date).local().startOf('day').toDate();
};

/**
 * Returns the end of day for a given date
 */
const endOfDay = (date: string | Date): Date => {
  return moment.utc(date).local().endOf('day').toDate();
};

/**
 * Add days to a date
 */
const addDays = (date: string | Date, days: number): Date => {
  return moment.utc(date).local().add(days, 'days').toDate();
};

/**
 * Subtract days from a date
 */
const subtractDays = (date: string | Date, days: number): Date => {
  return moment.utc(date).local().subtract(days, 'days').toDate();
};

/**
 * Get the difference in days between two dates
 */
const getDaysDifference = (dateA: string | Date, dateB: string | Date): number => {
  const a = moment.utc(dateA).local().startOf('day');
  const b = moment.utc(dateB).local().startOf('day');
  return Math.abs(a.diff(b, 'days'));
};

/**
 * Checks if two dates are the same day
 */
const isSameDay = (dateA: string | Date, dateB: string | Date): boolean => {
  return moment.utc(dateA).local().isSame(moment.utc(dateB).local(), 'day');
};

/**
 * Helper function to format wait time as "2h", "24h", etc.
 */
const formatWaitTime = (timestamp: string | null): string => {
  if (!timestamp) {
    return '';
  }
  const now = moment();
  const messageTime = moment.utc(timestamp).local();
  const diffHoursRaw = now.diff(messageTime, 'hours', true);
  if (diffHoursRaw < 0) {
    return '!';
  }
  const diffHours = Math.floor(diffHoursRaw);

  if (diffHours < 1) {
    const diffMinutes = Math.floor(now.diff(messageTime, 'minutes', true));
    return `${diffMinutes}m`;
  } else if (diffHours < 24) {
    return `${diffHours}h`;
  } else {
    const diffDays = Math.floor(now.diff(messageTime, 'days', true));
    return `${diffDays}d`;
  }
};

const humanizeDateTimeShort = (
  utcDate: string | Date,
  translator: (key: string) => string
): string => {
  const time = moment.utc(utcDate).local().format('HH:mm');
  return `${humanizeDateShort(utcDate, translator)} ${time}`;
};

// Numeric, locale-independent day-month-year (e.g. 03.04.2026).
const formatDateNumeric = (utcDate: string | Date): string =>
  moment.utc(utcDate).local().format('DD.MM.YYYY');

// Numeric, locale-independent day-month-year with 24h time (e.g. 03.04.2026 14:52).
const formatDateTimeNumeric = (utcDate: string | Date): string =>
  moment.utc(utcDate).local().format('DD.MM.YYYY HH:mm');

export {
  addDays,
  currentDateIsoFormat,
  dateToYYYYMMDD,
  endOfDay,
  formatDateNumeric,
  formatDateTimeNumeric,
  formatTime,
  formatWaitTime,
  getDaysDifference,
  humanizeDate,
  humanizeDateShort,
  humanizeDateTimeShort,
  humanizeTime,
  humanizeTimeLeft,
  isDateTodayOrPast,
  isFuture,
  isPast,
  isSameDay,
  isToday,
  secondsToMinutes,
  secondsToTimeString,
  startOfDay,
  subtractDays
};
